/**
 * OAuth2 scope definition with group support.
 */
export interface OAuth2ScopeDefinition {
    name: string;
    description: string;
    group: string;
    rights?: number[];
}

/**
 * Grouped scopes for UI display.
 */
export interface OAuth2ScopeGroup {
    group: string;
    scopes: { name: string; description: string }[];
}

/**
 * Central registry for OAuth2 scopes.
 * 
 * Allows modules to register their scopes at startup, avoiding circular dependencies.
 * The OAuth2 provider and Swagger documentation can read from this registry.
 * 
 * Usage:
 * ```typescript
 * // In your module's onModuleInit:
 * OAuth2ScopeRegistry.register({ name: 'jobs:read', description: 'Read jobs', group: 'Jobs' });
 * 
 * // In OAuth2 provider or Swagger setup:
 * const scopes = OAuth2ScopeRegistry.getAll();
 * const grouped = OAuth2ScopeRegistry.getGrouped();
 * ```
 */
export class OAuth2ScopeRegistry {

    private static scopes = new Map<string, OAuth2ScopeDefinition>();

    /**
     * Register a scope with its description and group.
     */
    static register(scope: OAuth2ScopeDefinition): void {

        this.scopes.set(scope.name, scope);
    }

    /**
     * Register multiple scopes at once.
     */
    static registerMany(scopes: OAuth2ScopeDefinition[]): void {

        for (const scope of scopes) {
            this.scopes.set(scope.name, scope);
        }
    }

    /**
     * Remove a scope from the registry (e.g. when a plan-gated feature is unavailable).
     */
    static unregister(scope: string): void {

        this.scopes.delete(scope);
    }

    /**
     * Get a scope's definition.
     */
    static get(scope: string): OAuth2ScopeDefinition | undefined {

        return this.scopes.get(scope);
    }

    /** Wildcard scope — grants access to all registered scopes */
    static readonly WILDCARD = '*';

    /**
     * Check if a scope is registered (or is the wildcard).
     */
    static has(scope: string): boolean {

        return scope === this.WILDCARD || this.scopes.has(scope);
    }

    /**
     * Get all registered scopes as a flat list, sorted by group then name.
     */
    static getAll(): OAuth2ScopeDefinition[] {

        return Array.from(this.scopes.values()).sort((a, b) => {
            const groupCompare = a.group.localeCompare(b.group);
            if (groupCompare !== 0) return groupCompare;
            return a.name.localeCompare(b.name);
        });
    }

    /**
     * Get all scopes as a record (for Swagger/OpenAPI).
     */
    static getAllAsRecord(): Record<string, string> {

        const sorted = this.getAll();
        return Object.fromEntries(sorted.map(s => [s.name, s.description]));
    }

    /**
     * Get scopes grouped and sorted for UI display.
     */
    static getGrouped(): OAuth2ScopeGroup[] {

        const grouped = new Map<string, { name: string; description: string }[]>();

        for (const scope of this.scopes.values()) {
            if (!grouped.has(scope.group)) {
                grouped.set(scope.group, []);
            }
            grouped.get(scope.group)!.push({ name: scope.name, description: scope.description });
        }

        // Sort scopes within each group
        for (const scopes of grouped.values()) {
            scopes.sort((a, b) => a.name.localeCompare(b.name));
        }

        // Sort groups and return
        return Array.from(grouped.entries())
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([group, scopes]) => ({ group, scopes }));
    }

    /**
     * Filter scope names to only those the user's rights allow.
     * A scope with no rights (or empty rights array) is unrestricted.
     * A scope is allowed if ANY of its rights are present in userRights.
     */
    static filterByRights(scopeNames: string[], userRights: number[]): string[] {

        return scopeNames.filter(name => {
            const scope = this.scopes.get(name);
            if (!scope?.rights?.length) return true;
            return scope.rights.some(r => userRights.includes(r));
        });
    }

    /**
     * Get all scope names.
     */
    static getNames(): string[] {

        return Array.from(this.scopes.keys()).sort();
    }

    /**
     * Validate that all requested scopes are registered.
     * Returns invalid scopes, or empty array if all valid.
     * The wildcard scope `*` is always valid.
     */
    static validateScopes(requestedScopes: string[]): string[] {

        return requestedScopes.filter(scope => !this.has(scope));
    }

    /**
     * Clear all scopes (mainly for testing).
     */
    static clear(): void {

        this.scopes.clear();
    }
}
